import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useGuestStore } from '../store'
import { apiClient } from '../services/apiClient'


export default function GuestQRRegistrationPage() {

  const { adminId } =
    useParams<{ adminId: string }>()

  const navigate = useNavigate()
  
  const guest = useGuestStore((state) => state.guest)
  const setGuest = useGuestStore((state) => state.setGuest)
  const setCurrentState = useGuestStore((state) => state.setCurrentState)
  const setStateData = useGuestStore((state) => state.setStateData)


  const [isLoading, setIsLoading] =
    useState(true)

  const [error, setError] =
    useState('')
    
  const [showResetDialog, setShowResetDialog] = useState(false)


  useEffect(() => {

    const initRegistration = async () => {

      try {

        // Проверяем, есть ли активная сессия гостя с завершенной регистрацией
        if (guest && guest.status === 'COMPLETED') {
          console.log('Found completed guest session:', guest)
          setShowResetDialog(true)
          setIsLoading(false)
          return
        }

        // Если нет гостя или сессия не завершена, создаем нового и переходим на registration
        if (!guest || guest.status !== 'COMPLETED') {
          console.log('Creating new guest and redirecting to registration')
          
          // Очищаем предыдущую сессию если есть
          if (guest) {
            setGuest(null)
            setCurrentState(null)
            setStateData({})
            localStorage.removeItem('guest')
          }
          
          // Создаем нового гостя
          const response = await apiClient.registerGuest(adminId)
          console.log('Guest created:', response)
          
          if (!response || !response.guest_id) {
            throw new Error('Не удалось создать гостя')
          }
          
          // Создаем объект Guest
          const guestId = typeof response.guest_id === 'number' 
            ? response.guest_id 
            : Number(response.guest_id)
          
          const newGuest = {
            id: guestId,
            hotel_id: adminId,
            documents: [],
            status: 'ACTIVE' as const,
            session_id: response.session_id,
            current_state: response.current_state
          }
          
          setGuest(newGuest)
          setStateData({ 
            session_id: response.session_id,
            current_state: response.current_state
          })
          
          // Переходим на страницу регистрации с FSM
          navigate(`/guest/${adminId}/registration`)
        }

        setIsLoading(false)


      } catch (err: any) {

        console.error(
          'Registration init error:',
          err
        )


        setError(
          'Ошибка инициализации регистрации'
        )


        setIsLoading(false)

      }

    }


    initRegistration()

  }, [adminId, guest, setGuest, setCurrentState, setStateData, navigate])
  
  const handleResetSession = async () => {
    try {
      if (guest?.id) {
        // Сбрасываем сессию на сервере
        await apiClient.resetGuestSession(guest.id)
      }
      
      // Очищаем локальное состояние
      setGuest(null)
      setCurrentState(null)
      setStateData({})
      localStorage.removeItem('guest')
      
      setShowResetDialog(false)
      console.log('Session reset successfully')
    } catch (err) {
      console.error('Failed to reset session:', err)
      // Даже если ошибка, очищаем локальное состояние
      setGuest(null)
      setCurrentState(null)
      setStateData({})
      localStorage.removeItem('guest')
      setShowResetDialog(false)
    }
  }

  /*
   * Состояние загрузки
   */

  if (isLoading) {

    return (

      <div className="min-h-screen bg-slate-50">


        <header className="border-b border-slate-200 bg-white">

          <div className="mx-auto flex max-w-3xl items-center px-4 py-4">


            <div className="flex items-center gap-3">


              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-xl">

                🏨

              </div>


              <div>


                <div className="font-bold tracking-tight text-slate-900">

                  HotelCheck

                </div>


                <div className="text-xs text-slate-500">

                  Онлайн-регистрация

                </div>

              </div>

            </div>

          </div>

        </header>


        <main className="flex min-h-[calc(100vh-73px)] items-center justify-center px-4">


          <div className="text-center">


            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />


            <p className="mt-4 text-sm text-slate-500">

              Инициализация регистрации...

            </p>

          </div>

        </main>

      </div>

    )

  }


  /*
   * Состояние ошибки
   */

  if (error) {

    return (

      <div className="min-h-screen bg-slate-50">


        <header className="border-b border-slate-200 bg-white">

          <div className="mx-auto flex max-w-3xl items-center px-4 py-4">


            <div className="flex items-center gap-3">


              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-xl">

                🏨

              </div>


              <div>


                <div className="font-bold tracking-tight text-slate-900">

                  HotelCheck

                </div>


                <div className="text-xs text-slate-500">

                  Онлайн-регистрация

                </div>

              </div>

            </div>

          </div>

        </header>


        <main className="flex min-h-[calc(100vh-73px)] items-center justify-center px-4">


          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">


            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 text-3xl">

              ⚠️

            </div>


            <h1 className="mt-5 text-xl font-bold text-slate-900">

              Не удалось загрузить страницу

            </h1>


            <p className="mt-2 leading-6 text-slate-500">

              {error}

            </p>


            <button

              onClick={() =>
                window.location.reload()
              }

              className="mt-6 w-full rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700"

            >

              Попробовать снова

            </button>

          </div>

        </main>

      </div>

    )

  }

  /*
   * Диалог сброса сессии
   */
  if (showResetDialog) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="border-b border-slate-200 bg-white">
          <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-xl">
                🏨
              </div>
              <div>
                <div className="font-bold tracking-tight text-slate-900">
                  HotelCheck
                </div>
                <div className="text-xs text-slate-500">
                  Онлайн-регистрация
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex min-h-[calc(100vh-73px)] items-center justify-center px-4">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-3xl">
              🔄
            </div>

            <h1 className="mt-5 text-xl font-bold text-slate-900">
              Предыдущая регистрация завершена
            </h1>

            <p className="mt-2 leading-6 text-slate-500">
              Вы уже завершали регистрацию ранее. Хотите начать новую регистрацию?
            </p>

            <div className="mt-6 space-y-3">
              <button
                onClick={handleResetSession}
                className="w-full rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700"
              >
                Начать новую регистрацию
              </button>
              
              <button
                onClick={() => navigate(`/guest/${adminId}/success`)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-5 py-3 font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Посмотреть результаты предыдущей
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  // Эта страница не должна рендериться в нормальном случае - сразу перенаправляем
  return null
}